import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSession } from '../hooks/useSession.tsx';
import { NOTICES_CHANGED_EVENT, listNotices } from './store.ts';
import type { StoredNotice } from './store.ts';
import { syncNotifications } from './sync.ts';

/**
 * React bindings for the notification store + sync loop.
 *
 * `useNotificationsSync` runs the pull loop through react-query: once
 * on mount and every five minutes after (the polling cadence agreed for
 * #116). The bell in the shell header keeps it mounted for the whole
 * authenticated session; the notifications page mounts it again, which
 * react-query dedupes onto the same cache entry. A failed sync is
 * silent by design — the store still holds the last good state, so the
 * UI shows locally persisted items instead of an error wall.
 *
 * `useStoredNotices` subscribes to the store's change event so every
 * consumer (bell dot, page list) re-reads the same localStorage truth
 * the moment any of them writes it.
 */

export const NOTIFICATIONS_SYNC_INTERVAL_MS = 5 * 60_000;

export function useNotificationsSync(): void {
  const { user, fetchImpl } = useSession();
  const scope = user?.orgId ?? null;
  useQuery({
    queryKey: ['notifications', 'sync', scope],
    queryFn: () => (scope === null ? Promise.resolve(0) : syncNotifications(scope, fetchImpl)),
    enabled: scope !== null,
    refetchInterval: NOTIFICATIONS_SYNC_INTERVAL_MS,
  });
}

export function useStoredNotices(scope: string | null): readonly StoredNotice[] {
  const [notices, setNotices] = useState<readonly StoredNotice[]>(() =>
    scope === null ? [] : listNotices(scope),
  );
  useEffect(() => {
    if (scope === null) {
      setNotices([]);
      return;
    }
    setNotices(listNotices(scope));
    function refresh(): void {
      if (scope !== null) {
        setNotices(listNotices(scope));
      }
    }
    window.addEventListener(NOTICES_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener(NOTICES_CHANGED_EVENT, refresh);
    };
  }, [scope]);
  return notices;
}
